import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";

/**
 * Cliente dos endpoints `/admin/scoring-weights` — gerencia versões de pesos
 * do scoring engine. Backend valida profile_slug=admin no JWT; chamadas de
 * usuário comum vão devolver 403 (component esconde a página, mas é boa
 * prática deixar a API ser a fonte da verdade).
 */

const BASE = `${SUPABASE_URL}admin/scoring-weights`;

export interface WeightsVersion {
  id: string;
  name: string;
  weights: Record<string, number>;
  source: "manual" | "auto_regression" | "seed";
  is_active: boolean;
  training_metrics: Record<string, unknown> | null;
  trained_at: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

export interface FeatureInfo {
  weight_key: string;
  default_value: number;
}

export function listFeatures(): Promise<FeatureInfo[]> {
  return apiFetch<FeatureInfo[]>(`${BASE}/available-features`);
}

export function listVersions(params: { name?: string; limit?: number } = {}): Promise<WeightsVersion[]> {
  const q = new URLSearchParams();
  if (params.name) q.append("name", params.name);
  if (params.limit) q.append("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<WeightsVersion[]>(`${BASE}${qs ? `?${qs}` : ""}`);
}

export function listActive(): Promise<WeightsVersion[]> {
  return apiFetch<WeightsVersion[]>(`${BASE}/active`);
}

export function createVersion(payload: {
  name: string;
  weights: Record<string, number>;
  notes?: string;
  activate_now?: boolean;
}): Promise<WeightsVersion> {
  return apiFetch<WeightsVersion>(BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function activateVersion(id: string): Promise<WeightsVersion> {
  return apiFetch<WeightsVersion>(`${BASE}/${id}/activate`, { method: "POST" });
}

export interface RetrainResult {
  ok: boolean;
  version_id: string;
  metrics: Record<string, unknown>;
  activated: boolean;
  triggered_by: string;
}

export function triggerRetrain(activate = true): Promise<RetrainResult> {
  return apiFetch<RetrainResult>(`${BASE}/retrain?activate=${activate}`, { method: "POST" });
}
